import cx from 'classnames';
import {filter, find, map, some, toLower} from 'lodash';
import moment from 'moment';
import {useCallback, useEffect, useMemo, useState, useContext} from 'react';
import {useTranslation} from 'react-i18next';
import {WritingOptions} from 'xlsx';

import {sessionHistoryApi} from 'api';
import {
    InformationModal, DataWrapper, Select, Checkbox, Icon, Button, StarRating
} from 'components';
import {APP_NAME} from 'constants/app';
import {UserContext} from 'context';
import {useTask} from 'hooks';
import {Option, SessionResultInformationDetails} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {UserSummary} from 'types';
import {dateUtils, settingsUtils} from 'utils';
import {lazyModules} from 'utils/lazy-modules';
import {getParticipantName, transformUtils} from 'utils/transform';

import {ActivityMetricItem} from '../ActivityMetricItem';
import {SessionsResultInformation} from '../SessionsResultInformation';
import {REQUEST_SESSION_HISTORY_DATE_FORMAT} from '../constants';
import {ActivityChartType, ActivityFilterType} from '../enums';
import {AGENT_METRICS_CONFIGS} from '../metric-config';
import {ActivityFilters, AgentStatisticMap} from '../types';

import {ActivityStatisticChart} from './ActivityStatisticChart';

const T_KEYS = TRANSLATION_KEYS.USER.ACTIVITY.AGENTS_STATISTIC;
const T_KEYS_COMMON = TRANSLATION_KEYS.COMMON;
const T_KEYS_ACTIVITY = TRANSLATION_KEYS.USER.ACTIVITY;
const T_KEYS_STATISTICS_FAILED = TRANSLATION_KEYS.USER.ACTIVITY.ERROR.STATISTICS_FAILED;

type Props = {
    agents: UserSummary[];
    filters: ActivityFilters | null;
    downloadFileDisabled?: boolean;
    setAvailableFilters: (filters: ActivityFilterType[]) => void;
    onToggleLoading: (value: boolean) => void;
};

export const AgentsStatistic = ((
    {agents, filters, downloadFileDisabled, setAvailableFilters, onToggleLoading}: Props) => {
    const {t} = useTranslation();
    const {settings} = useContext(UserContext);
    const canSeeFilePipeline = useMemo(() => settingsUtils.canSeeFilePipeline(settings), [settings]);
    const [agentsMap, setAgentsMap] = useState({} as AgentStatisticMap);
    const [isLoading, setIsLoading] = useState(false);
    const [chartDates, setChartDates] = useState([]);
    const [isLoadingError, setIsLoadingError] = useState(false);
    const [activeAgentsOnly, setActiveAgentsOnly] = useState(true);
    const [chartType, setChartType] = useState<ActivityChartType>(ActivityChartType.SessionResult);
    const [sessionsDetailsModalProps, setSessionsDetailsModalProps] = useState<{
        open: boolean,
        title: string,
        sessionResultInformationDetails: SessionResultInformationDetails
    }>({open: false, sessionResultInformationDetails: null, title: ''});

    const {dateFormat} = useMemo(() => settings.common, [settings.common]);
    const fileDateFormat = useMemo(() => transformUtils.getFileDateFormat(dateFormat), [dateFormat]);

    const withRatingInReport = useMemo(() => some(agentsMap, (agentStatistic) => agentStatistic.total.rating.count > 0), [agentsMap]);

    const changeLoading = useCallback((value) => {
        setIsLoading(value);
        onToggleLoading(value);
    }, [onToggleLoading]);

    useEffect(() => {
        setAvailableFilters([
            ActivityFilterType.StartDate,
            ActivityFilterType.EndDate,
            ActivityFilterType.Group,
            ActivityFilterType.Subgroup,
            ActivityFilterType.Result,
            ActivityFilterType.ResultInformation,
            ActivityFilterType.User,
            ActivityFilterType.ClientDevice,
            ActivityFilterType.UserInfoId
        ]);
    }, [setAvailableFilters]);

    useEffect(() => {
        if (filters) {
            const {startDate, endDate} = filters;
            setChartDates(dateUtils.getRange(startDate, endDate, 'days', REQUEST_SESSION_HISTORY_DATE_FORMAT, {includeEndDate: true}));
        }
    }, [dateFormat, filters]);

    const {startTask} = useTask(sessionHistoryApi.getAgentsStatistic, {
        onComplete: (data: AgentStatisticMap) => {
            setIsLoadingError(false);
            setAgentsMap(data);
        },
        notification: {
            enabled: true,
            labels: {
                error: t(T_KEYS_STATISTICS_FAILED)
            }
        },
        onToggleLoading: changeLoading,
        onError: () => {
            setIsLoadingError(true);
        }
    });

    const availableConfigs = useMemo(() =>
        filter(AGENT_METRICS_CONFIGS, (config) => !config.isHidden?.({settings})), [settings]);

    const availableMetrics = useMemo(() => map(availableConfigs, (config) => config.metric), [availableConfigs]);

    const chartTypeOptions = useMemo(() => [{
        id: ActivityChartType.Total,
        name: t(T_KEYS.CHART_TYPE.TOTAL)
    }, {
        id: ActivityChartType.SessionResult,
        name: t(T_KEYS.CHART_TYPE.SESSION_RESULT)
    }, ...(canSeeFilePipeline ? [{
        id: ActivityChartType.FilePipelineStatus,
        name: t(T_KEYS.CHART_TYPE.FILE_PIPELINE_STATUS)
    }] : []), {
        id: ActivityChartType.AvgDuration,
        name: t(T_KEYS.CHART_TYPE.AVG_DURATION)
    }] as Option[], [canSeeFilePipeline, t]);

    const chartTypeValue = useMemo(() => find(chartTypeOptions, {id: chartType}), [chartType, chartTypeOptions]);

    const filteredAgents = useMemo(() => {
        if (!activeAgentsOnly) {
            return agents;
        }
        return filter(agents, (agent) => {
            const agentStatistic = agentsMap?.[agent.eid];
            return agentStatistic?.total?.answered > 0;
        });
    }, [agents, agentsMap, activeAgentsOnly]);

    const numberOfAgents = useMemo(() =>
        toLower(`
            ${isLoading ? 0 : filteredAgents.length} ${t(T_KEYS_COMMON.ALL.OF)} ${agents.length} ${t(T_KEYS_COMMON.SESSION.LABELS.AGENTS)}
        `),
    [isLoading, agents.length, filteredAgents.length, t]);

    const downloadReport = useCallback(async () => {
        if (!filters) {
            return;
        }

        const xlsx = await lazyModules.xlsx();
        if (!xlsx) {
            return;
        }

        const {startDate, endDate} = filters;

        const wb = xlsx.utils.book_new();
        const sheetName = t(T_KEYS.REPORT.SHEET_NAME);
        const fileExtension = 'xlsx';

        const from = moment(startDate).format(fileDateFormat);
        const to = moment(endDate).format(fileDateFormat);
        const fileName = `${t(T_KEYS.REPORT.FILE_NAME_PREFIX)} ${from}-${to}.${fileExtension}`;
        const writeOpts: WritingOptions = {bookType: fileExtension, bookSST: true, type: 'binary', sheet: sheetName};
        const agentNameHeader = t(T_KEYS_COMMON.SESSION.LABELS.AGENT);

        const json = map(filteredAgents, (agent) => {
            const agentStatistic = agentsMap[agent.eid];
            const res: {[key: string]: string | number} = {[agentNameHeader]: getParticipantName(agent)};
            const result = availableConfigs.reduce((acc, config) => {
                acc[t(config.title)] = agentStatistic?.total ? config.valueGetter(agentStatistic.total) : null;
                return acc;
            }, res);
            if (withRatingInReport) {
                result[t(T_KEYS_COMMON.SESSION.LABELS.RATING)] = +agentStatistic.total.rating.average.toFixed(2);
            }
            return result;
        });

        const header = map(availableConfigs, (config) => t(config.title));
        if (withRatingInReport) {
            header.unshift(t(T_KEYS_COMMON.SESSION.LABELS.RATING));
        }
        header.unshift(agentNameHeader);
        const ws = xlsx.utils.json_to_sheet(json, {header});

        wb.SheetNames.push(sheetName);
        wb.Sheets[sheetName] = ws;
        wb.Props = {Author: APP_NAME};

        xlsx.writeFile(wb, fileName, writeOpts);
    }, [t, filters, fileDateFormat, filteredAgents, availableConfigs, withRatingInReport, agentsMap]);

    useEffect(() => {
        if (startTask && filters) {
            startTask({
                ...filters,
                startDate: filters.startDate.format(REQUEST_SESSION_HISTORY_DATE_FORMAT),
                endDate: filters.endDate.format(REQUEST_SESSION_HISTORY_DATE_FORMAT),
            });
        }
    }, [filters, startTask]);

    if (!filters) {
        return null;
    }

    return (
        <div className='tw-relative'>
            <div className='tw-inline-flex tw-items-end -md:tw-grid -md:tw-grid-cols-2 -md:tw-gap-y-3 -md:tw-gap-x-1 tw-mb-4'>
                <div className='tw-min-w-6 tw-mt-3 tw-mr-4 tw-self-center'>
                    {numberOfAgents}
                </div>

                <Checkbox
                    size='small'
                    name='active-agents-only'
                    className='tw-mr-4 tw-mt-3 tw-self-center'
                    checked={activeAgentsOnly}
                    label={t(T_KEYS.ACTIVE_AGENTS_ONLY)}
                    onChange={setActiveAgentsOnly}
                />
                <Select
                    className='tw-w-60 -md:tw-w-auto tw-mr-4'
                    placeholder={t(T_KEYS.CHART_TYPE.TITLE)}
                    options={chartTypeOptions}
                    value={chartTypeValue}
                    onChange={(value) => setChartType(value.id as ActivityChartType)}
                />
                <Button loading={isLoading} icon='arrow-down' disabled={downloadFileDisabled} onClick={downloadReport}>
                    {t(T_KEYS.DOWNLOAD)}
                </Button>
            </div>

            <DataWrapper isLoading={isLoading} isError={isLoadingError} noData={!filteredAgents?.length}>
                {map(filteredAgents, (agent) => {
                    const agentStatistic = agentsMap?.[agent.eid];
                    if (!agentStatistic) {
                        return null;
                    }
                    const hasAnsweredSessions = agentStatistic.total.answered > 0;
                    const hasRating = agentStatistic.total.rating.count > 0;

                    return (
                        <div key={agent.eid} className='tw-bg-light tw-p-2 tw-my-4 tw-overflow-hidden'>
                            <div className='tw-flex tw-items-center tw-my-2 tw-ml-5'>
                                <Icon name='user' size='lg' className='tw-mr-2' />
                                <div className='tw-flex tw-flex-wrap'>
                                    <div className={cx('sm:tw-ts-h5 lg:tw-ts-h2', {'tw-mr-2': hasRating})}>{getParticipantName(agent)}</div>
                                    {hasRating &&
                                        <div className='tw-flex tw-items-center'>
                                            <StarRating
                                                value={agentStatistic.total.rating.average}
                                                size='medium'
                                                className='-lg:tw-hidden'
                                            />
                                            <StarRating value={agentStatistic.total.rating.average} className='lg:tw-hidden' />
                                            <div className='tw-ml-1 lg:tw-ts-h5'>{`(${agentStatistic.total.rating.count})`}</div>
                                        </div>}
                                </div>
                            </div>

                            <div className='tw-flex -xl:tw-flex-col xl:tw-items-center tw-w-full'>
                                <div className='tw-flex tw-flex-col xl:tw-order-2 xl:tw-w-112 xl:tw-flex-shrink-0 tw-ml-4 -xl:tw-mb-4'>
                                    {map(availableMetrics, (metric) => (
                                        <ActivityMetricItem
                                            key={metric}
                                            viewType='row'
                                            metric={metric}
                                            statistic={agentStatistic.total}
                                        />
                                    ))}
                                    <div className='tw-flex tw-items-center tw-p-2'>
                                        <Icon size='sm' name='check-circle' color='success' className='tw-mr-2' />
                                        <div
                                            className='tw-text-primary tw-cursor-pointer'
                                            onClick={() => setSessionsDetailsModalProps({
                                                open: true,
                                                title: t(T_KEYS_ACTIVITY.SESSION_RESULT_INFORMATION_DETAILS.SUCCESSFUL_DETAILS),
                                                sessionResultInformationDetails: agentStatistic.total.successfulSessionResultDetails
                                            })}
                                        >
                                            {t(T_KEYS_ACTIVITY.SESSION_RESULT_INFORMATION_DETAILS.SUCCESSFUL_DETAILS)}
                                        </div>
                                    </div>
                                    <div className='tw-flex tw-items-center tw-p-2'>
                                        <Icon size='sm' name='x-circle' color='alert' className='tw-mr-2' />
                                        <div
                                            className='tw-text-primary tw-cursor-pointer'
                                            onClick={() => setSessionsDetailsModalProps({
                                                open: true,
                                                title: t(T_KEYS_ACTIVITY.SESSION_RESULT_INFORMATION_DETAILS.UNSUCCESSFUL_DETAILS),
                                                sessionResultInformationDetails: agentStatistic.total.unsuccessfulSessionResultDetails
                                            })}
                                        >
                                            {t(T_KEYS_ACTIVITY.SESSION_RESULT_INFORMATION_DETAILS.UNSUCCESSFUL_DETAILS)}
                                        </div>
                                    </div>
                                </div>
                                {hasAnsweredSessions &&
                                    <div className='tw-h-80 xl:tw-order-1 xl:tw-flex-grow tw--ml-8 tw-min-w-0'>
                                        <ActivityStatisticChart
                                            statistic={agentStatistic}
                                            chartType={chartType}
                                            dates={chartDates}
                                            format={dateFormat}
                                        />
                                    </div>}
                            </div>
                        </div>
                    );
                })}
            </DataWrapper>
            <InformationModal
                open={sessionsDetailsModalProps.open}
                title={sessionsDetailsModalProps.title}
                fullWidth
                maxWidth='sm'
                onClose={() => setSessionsDetailsModalProps({
                    open: false,
                    title: '',
                    sessionResultInformationDetails: null
                })}
            >
                <SessionsResultInformation
                    sessionResultInformationDetails={sessionsDetailsModalProps.sessionResultInformationDetails}
                />
            </InformationModal>
        </div>
    );
});
